/**
 * Types and the arithmetic behind the Pricing Intelligence instrument.
 *
 * DIVISION OF LABOUR — the thing to understand before changing anything here.
 *
 * The server owns the comparable POOL. The client owns the subject's POSITION
 * within it. That line is not an optimisation, it is the honest boundary:
 *
 *   - Which comparables count is a judgement about evidence. It needs the
 *     PostGIS radius query, the selector refusals (VAL-B1) and the
 *     drop-on-missing-comparator rule (VAL-B2). That belongs to the server.
 *   - Where a given asking price sits among a FIXED set of comparables is
 *     arithmetic over numbers already in hand. No round trip makes it truer.
 *
 * So dragging the asking price recalculates instantly and locally, while
 * editing the property's specs re-asks the server. Anything that would change
 * pool membership must go back to the server, or the page starts quietly
 * lying about what its numbers are made of.
 */

import type {
  PricePosition,
  SubjectAnalysis,
  SubjectComparable,
  SubjectDistribution,
} from '@/types/marketPricing'

export type {
  PricePosition,
  PricingConfidence,
  SubjectAnalysis,
  SubjectAnalysisRequest,
  SubjectComparable,
  SubjectDistribution,
  SubjectTarget,
} from '@/types/marketPricing'

/**
 * Comparables reduced to the two numbers the position math needs.
 *
 * Mirrors `toWeightedEvidence` and `normalizedWeight` in
 * `backend/src/modules/property-valuation/application/analysis-service.js`:
 * a non-finite or non-positive weight becomes 0.0001 rather than zero, so a
 * comparable can never be silently erased from the denominator, and a price
 * that is not a positive finite number drops out entirely.
 */
export function toWeightedEvidence(
  comparables: SubjectComparable[],
): { value: number; weight: number }[] {
  return comparables
    .map((comparable) => {
      const weight = Number(comparable.weight)
      return {
        value: Number(comparable.normalized_price),
        weight: Number.isFinite(weight) && weight > 0 ? weight : 0.0001,
      }
    })
    .filter((entry) => Number.isFinite(entry.value) && entry.value > 0)
}

/**
 * Where `price` sits among the weighted comparables, 0-100.
 *
 * A DELIBERATE MIRROR of `weightedPercentileRank`
 * (`analysis-service.js:388`) — strictly-less-than, weight-summed, two
 * decimal places. It is duplicated because the asking-price rule has to move
 * with the pointer, and a network round trip per pixel is not a thing.
 *
 * The duplication is the risk: if the two formulas drift, the number an agent
 * drags to and the number the server would have reported disagree, which is
 * exactly the class of defect this workstream exists to prevent.
 * `pricingIntelligence.test.ts` pins them to one fixture. Change one, change
 * both, and make that test prove it.
 */
export function weightedPercentileRank(
  comparables: SubjectComparable[],
  price: number,
): number | null {
  const entries = toWeightedEvidence(comparables)
  if (!entries.length) return null
  const totalWeight = entries.reduce((sum, entry) => sum + entry.weight, 0)
  if (totalWeight <= 0) return null
  const belowWeight = entries.reduce(
    (sum, entry) => sum + (entry.value < price ? entry.weight : 0),
    0,
  )
  return Number(((belowWeight / totalWeight) * 100).toFixed(2))
}

/**
 * Mirrors `classifyPosition` in the analysis service: inside 5% of the median
 * counts as at-market. The band exists because a median computed from a few
 * dozen listings does not carry a third significant figure, and rendering a
 * 1% difference as "above market" would be false precision.
 */
export function classifyPosition(vsMedianPercent: number): PricePosition {
  if (vsMedianPercent < -5) return 'below'
  if (vsMedianPercent > 5) return 'above'
  return 'at'
}

export interface LivePosition {
  percentile: number | null
  vsMedianPercent: number
  position: PricePosition
}

/** The full local recomputation performed on every pointer move. */
export function positionFor(analysis: SubjectAnalysis, price: number): LivePosition {
  const median = analysis.distribution?.median ?? 0
  const vsMedianPercent = median > 0 ? ((price - median) / median) * 100 : 0
  return {
    percentile: weightedPercentileRank(analysis.comparables, price),
    vsMedianPercent: Number(vsMedianPercent.toFixed(2)),
    position: classifyPosition(vsMedianPercent),
  }
}

/**
 * The price axis the instrument is drawn against.
 *
 * Padded past the observed extremes so an asking price outside the comparable
 * range stays visible and still draggable — an agent arguing for a price above
 * everything currently on the market is a real conversation, and an axis that
 * clipped it would hide the very thing being discussed.
 */
export function priceDomain(
  distribution: SubjectDistribution,
  askingPrice: number | null,
): { min: number; max: number } {
  const values = [distribution.lowest, distribution.highest]
  if (askingPrice != null && Number.isFinite(askingPrice)) values.push(askingPrice)
  const low = Math.min(...values)
  const high = Math.max(...values)
  const span = high - low || Math.max(high, 1) * 0.2
  return { min: Math.max(0, low - span * 0.15), max: high + span * 0.15 }
}

/** Compact money for axis ticks and headline readouts. */
export function formatMoney(value: number | null | undefined, currency = 'USD'): string {
  if (value == null || !Number.isFinite(Number(value))) return '—'
  const num = Number(value)
  const symbol = currency === 'USD' ? '$' : ''
  const suffix = currency === 'USD' ? '' : ` ${currency}`
  if (Math.abs(num) >= 1_000_000) return `${symbol}${(num / 1_000_000).toFixed(2)}M${suffix}`
  if (Math.abs(num) >= 1_000) return `${symbol}${Math.round(num / 1_000)}K${suffix}`
  return `${symbol}${Math.round(num).toLocaleString()}${suffix}`
}

export function formatExact(value: number | null | undefined, currency = 'USD'): string {
  if (value == null || !Number.isFinite(Number(value))) return '—'
  const symbol = currency === 'USD' ? '$' : ''
  const suffix = currency === 'USD' ? '' : ` ${currency}`
  return `${symbol}${Math.round(Number(value)).toLocaleString()}${suffix}`
}

/**
 * Mark shape per comparable source.
 *
 * Source is encoded by SHAPE, not colour. Two reasons, and the second is the
 * one that matters: the design system has no data-series colour tokens (see
 * `SourceMark` — "brand color only inside logo/wordmark"), and shape survives
 * greyscale, colour blindness and a laptop screen angled across a table,
 * which is where this page gets used.
 */
export type MarkShape = 'filled' | 'hollow' | 'ringed'

export function markShapeFor(source: string | null | undefined): MarkShape {
  const code = String(source || '').toLowerCase()
  // Agent-reported sold prices that survived Platform Admin review are the
  // strongest evidence in the pool, so they get the most emphatic mark.
  if (code.includes('agent_report') || code.includes('sold')) return 'ringed'
  if (code === 'internal' || code.includes('wingcaster')) return 'filled'
  return 'hollow'
}

export const MARK_LEGEND: { shape: MarkShape; label: string }[] = [
  { shape: 'ringed', label: 'Verified sold price' },
  { shape: 'filled', label: 'On WingCaster' },
  { shape: 'hollow', label: 'Portal listing' },
]

/**
 * Property types, split by use category.
 *
 * Use category and type are SELECTORS, not comparators: an apartment is not
 * compared to a villa and a shop is not compared to a flat, so the agent
 * chooses the category first and the pool is built inside it. Values are the
 * app's existing taxonomy (`src/types/index.ts`), not a new vocabulary.
 */
export const USE_CATEGORIES = [
  {
    id: 'residential',
    label: 'Residential',
    types: ['apartment', 'villa', 'townhouse', 'studio', 'penthouse'],
  },
  {
    id: 'commercial',
    label: 'Commercial',
    types: ['office', 'shop'],
  },
] as const

export type UseCategoryId = (typeof USE_CATEGORIES)[number]['id']

export function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}
